import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, Eye } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PreviewSection from '../PresetPage/PreviewSection';
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from '../../editor/nodes/PlaygroundNodes';
import { processMathEquation } from '../../components/utils/latexParser';


function CreateExamModal({ 
  isOpen, 
  onClose, 
  stampId, 
  buttonRect,
  onSuccess, 
  selectedAddressesForExam, 
  setSelectedAddressesForExam, 
  examType, 
  setExamType, 
  deltaSelections, 
  setDeltaSelections,
  treeData,
  hierarchicalSelections,
  setHierarchicalSelections
}) {
  const { getAccessToken } = useAuth();
  const [isMinimized, setIsMinimized] = useState(false);
  const [difficulty, setDifficulty] = useState('1.0');
  const [totalProblems, setTotalProblems] = useState(5);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isPresetDropdownOpen, setIsPresetDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitCooldown, setIsSubmitCooldown] = useState(false); // 생성 버튼 더블클릭 방지(1초)
  const submitCooldownTimerRef = useRef(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewPresetData, setPreviewPresetData] = useState(null);
  const [previewProblemDetails, setPreviewProblemDetails] = useState({});
  const [solvedExceptionMode, setSolvedExceptionMode] = useState(false); // 중복 출제 방지 모드
  const [reviewList, setReviewList] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [isReviewDropdownOpen, setIsReviewDropdownOpen] = useState(false);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const presetDropdownRef = useRef(null);
  const reviewDropdownRef = useRef(null);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (presetDropdownRef.current && !presetDropdownRef.current.contains(e.target)) {
        setIsPresetDropdownOpen(false);
      }
      if (reviewDropdownRef.current && !reviewDropdownRef.current.contains(e.target)) {
        setIsReviewDropdownOpen(false);
      }
    };
    if (isPresetDropdownOpen || isReviewDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isPresetDropdownOpen, isReviewDropdownOpen]);

  // 유틸리티 함수들
  const getAddressParts = (address) => address.split('/');
  
  const getParentAddress = (address) => {
    const parts = getAddressParts(address);
    return parts.slice(0, -1).join('/');
  };

  const getChildAddresses = (parentAddress, depth) => {
    const depthData = {
      'd1': treeData?.field_info?.d1_y_axis || [],
      'd2': treeData?.field_info?.d2_y_axis || [],
      'd3': treeData?.field_info?.d3_y_axis || [],
      'd4': treeData?.field_info?.d4_y_axis || []
    };

    const children = new Set();
    const parentParts = getAddressParts(parentAddress);

    depthData[depth].forEach(item => {
      const itemAddress = item[2].replace(/_/g, '/');
      const itemParts = getAddressParts(itemAddress);
      
      if (itemParts.slice(0, parentParts.length).join('/') === parentAddress) {
        children.add(itemAddress);
      }
    });

    return children;
  };

  const isAllChildrenSelected = (parentAddress, depth) => {
    const children = getChildAddresses(parentAddress, getNextDepth(depth));
    return Array.from(children).every(child => 
      hierarchicalSelections[getNextDepth(depth)].has(child)
    );
  };

  const getNextDepth = (currentDepth) => {
    const depths = ['d1', 'd2', 'd3', 'd4'];
    const currentIndex = depths.indexOf(currentDepth);
    return depths[currentIndex + 1] || currentDepth;
  };

  const getPreviousDepth = (currentDepth) => {
    const depths = ['d1', 'd2', 'd3', 'd4'];
    const currentIndex = depths.indexOf(currentDepth);
    return depths[currentIndex - 1] || currentDepth;
  };

  const optimizeSelections = () => {
    const optimized = new Set();
    
    // 각 depth별로 최적화
    ['d4', 'd3', 'd2', 'd1'].forEach(depth => {
      hierarchicalSelections[depth].forEach(address => {
        const parentAddress = getParentAddress(address);
        
        // 이미 상위 주소가 선택되었다면 건너뛰기
        if (Array.from(optimized).some(opt => address.startsWith(opt))) {
          return;
        }

        // 모든 형제 주소가 선택되었다면 부모 주소 추가
        if (isAllChildrenSelected(parentAddress, getPreviousDepth(depth))) {
          optimized.add(parentAddress);
        } else {
          optimized.add(address);
        }
      });
    });

    return Array.from(optimized);
  };

  const handleSelectionChange = (address, depth) => {
    setHierarchicalSelections(prev => {
      const newSelections = { ...prev };
      const currentSet = new Set(newSelections[depth]);

      if (currentSet.has(address)) {
        // 선택 해제
        currentSet.delete(address);
        // 하위 주소들도 모두 해제
        ['d2', 'd3', 'd4'].forEach(d => {
          if (d > depth) {
            newSelections[d] = new Set(
              Array.from(newSelections[d]).filter(addr => !addr.startsWith(address))
            );
          }
        });
      } else {
        // 선택
        currentSet.add(address);
        // 하위 주소들 자동 선택
        if (depth !== 'd4') {
          const childAddresses = getChildAddresses(address, getNextDepth(depth));
          childAddresses.forEach(childAddr => {
            newSelections[getNextDepth(depth)].add(childAddr);
          });
        }
      }

      newSelections[depth] = currentSet;
      return newSelections;
    });

    // 최적화된 주소 목록 업데이트
    const optimizedAddresses = optimizeSelections();
    setSelectedAddressesForExam(optimizedAddresses);
  };

  // 프리셋 목록 불러오기
  const fetchPresets = async () => {
    try {
      setIsLoading(true);
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/bystamp?stamp_id=${stampId}`,
        {
          headers: { 'Authorization': token }
        }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      setPresets(data.data || []);
    } catch (error) {
      console.error('커스텀 목록 불러오기 실패:', error);
      alert('커스텀 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 오답노트 목록 불러오기
  const fetchReviewList = async () => {
    if (!stampId) return;
    setIsLoadingReviews(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/reviews?stamp_id=${stampId}`,
        { headers: { 'Authorization': token } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setReviewList(data.data || []);
    } catch (error) {
      console.error('오답노트 목록 불러오기 실패:', error);
      setReviewList([]);
    } finally {
      setIsLoadingReviews(false);
    }
  };

  // 시험 유형이 변경될 때 프리셋/오답노트 목록 불러오기
  useEffect(() => {
    if (examType === 'custom' && stampId) {
      fetchPresets();
    }
    if (examType === 'epsilon' && stampId) {
      fetchReviewList();
    }
  }, [examType, stampId]);

  const startSubmitCooldown = () => {
    setIsSubmitCooldown(true);
    if (submitCooldownTimerRef.current) {
      clearTimeout(submitCooldownTimerRef.current);
    }
    submitCooldownTimerRef.current = setTimeout(() => {
      setIsSubmitCooldown(false);
      submitCooldownTimerRef.current = null;
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (submitCooldownTimerRef.current) {
        clearTimeout(submitCooldownTimerRef.current);
        submitCooldownTimerRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1초 더블클릭 방지 + 제출 중 중복 요청 방지
    if (isSubmitting || isSubmitCooldown) return;

    try {
      const token = await getAccessToken();
      let endpoint = `${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/`;
      let body = {};

      switch (examType) {
        case 'alpha':
          endpoint += 'alpha';
          body = {
            difficulty: parseFloat(difficulty) * 10,
            total_problems: totalProblems
          };
          break;
        case 'beta':
        case 'gamma':
          endpoint += examType;
          if (selectedAddressesForExam.length === 0) {
            alert('최소 1개 이상의 유형을 선택해주세요.');
            return;
          }
          body = {
            pick_address: selectedAddressesForExam,
            total_problems: totalProblems
          };
          break;
        case 'delta':
          endpoint += 'delta';
          if (deltaSelections.length === 0) {
            alert('최소 1개 이상의 중유형과 레벨을 선택해주세요.');
            return;
          }
          body = {
            selections: deltaSelections.map(selection => {
              const [address, level] = selection.split(',');
              return { address, level: parseInt(level) };
            })
          };
          break;
        case 'custom':
          if (!selectedPreset) {
            alert('커스텀을 선택해주세요.');
            return;
          }
          endpoint += 'preset';
          body = {
            preset_id: selectedPreset.preset_id
          };
          break;
        case 'epsilon':
          if (!selectedReview) {
            alert('오답노트를 선택해주세요.');
            return;
          }
          endpoint += 'epsilon';
          body = {
            error_note_id: selectedReview.errnotes?.[0]?.note_id
          };
          break;
        default:
          return;
      }

      // 중복 출제 방지 모드가 켜져 있으면 추가.
      //
      // ⚠ 여기는 **레거시 출제 경로**다(POST /api/exams/{stamp_id}/alpha|preset|epsilon).
      // 이 경로의 solved_exception 은 스탬프(Exams) 기반이라 **신규 시험지(exam-papers) 기록을 보지
      // 않는다** — 신규 시험지에서 마감된 문항은 여기서 제외되지 않고 다시 출제된다.
      // 2026-08-29 백엔드 회신 §1 2-4 가 「미반영이 맞다」고 확인했고, 이번에 바뀐 것은 get-pids 뿐이다
      // (docs/exam-builder-v2/BE-20260829-reply-fe-0828.md).
      //
      // 그래서 아래 체크박스 문구(「문제 중복 출제 방지 모드 / 이미 풀었던 문제를 제외하고…」)는
      // 다른 출제 화면의 문구와 기계적으로 통일하지 않는다. 여기는 스탬프 기준이라 문구를 바꾸는 순간
      // 화면이 거짓말을 한다 — 제외되지 않은 문제를 제외했다고 말하게 된다.
      if (solvedExceptionMode) {
        body.solved_exception = true;
      }

      setIsSubmitting(true);
      startSubmitCooldown();

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 409) {
          const data = await response.json();
          
          // preset 모드에서 409 에러는 중복 문제 경고만 표시
          if (examType === 'custom' && solvedExceptionMode) {
            alert(`⚠️ 주의: 선택한 프리셋에 이미 풀었던 문제가 포함되어 있습니다.\n\n프리셋 모드는 미리 정해진 문제 세트이므로 중복 문제를 피할 수 없습니다.\n계속 진행하시려면 '중복 출제 방지 모드'를 끄고 다시 시도해주세요.`);
          } else {
            alert(`시험 생성 실패: ${data.message}\n실패한 선택: ${data.data?.fail_list?.join(', ') || '없음'}`);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return;
      }

      alert('시험이 생성되었습니다.');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('시험 생성 오류:', error);
      alert('시험 생성 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 모달이 열리거나 닫힐 때 상태 초기화
  useEffect(() => {
    if (isOpen) {
      // 모달이 열릴 때 현재 모드에 맞는 리스트 다시 fetch
      if (examType === 'custom' && stampId) fetchPresets();
      if (examType === 'epsilon' && stampId) fetchReviewList();
    } else {
      setHierarchicalSelections({
        d1: new Set(),
        d2: new Set(),
        d3: new Set(),
        d4: new Set()
      });
      setSelectedAddressesForExam([]);
      setSelectedPreset(null);
      setSelectedReview(null);
      setSolvedExceptionMode(false); // 중복 출제 방지 모드 초기화
    }
  }, [isOpen]);

  const getDepthFromAddress = (address) => {
    if (!address) return null;
    const parts = address.split('/');
    switch(parts.length) {
      case 2: return 'd1';
      case 3: return 'd2';
      case 4: return 'd3';
      case 5: return 'd4';
      default: return null;
    }
  };

  if (!isOpen) return null;

  // 난이도 레벨 표시 헬퍼 함수
  const getDifficultyLabel = (difficulty) => {
    // difficulty는 10-50 사이의 값으로 표현됨
    const level = Math.round(difficulty / 10) / 10;
    return level.toFixed(1);
  };

  // 난이도 요약 정보 렌더링
  const renderSummary = (summary) => {
    if (!summary || !Array.isArray(summary)) return null;
    
    return (
      <div className="flex items-center gap-1">
        {summary.map((count, idx) => (
          <div key={idx} className="flex flex-col items-center">
            <span className="text-xs text-gray-600">Lv{idx + 1}</span>
            <span className={`text-sm font-medium ${count > 0 ? 'text-primary' : 'text-gray-400'}`}>
              {count}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Lexical 데이터를 HTML로 변환하는 함수
  const convertLexicalToHtml = (lexicalData) => {
    const editor = createEditor({
      nodes: [...PlaygroundNodes]
    });

    const processNode = (node) => {
      if (node.type === 'equation') {
        node.equation = processMathEquation(node.equation);
      } else if (node.type === 'box-content') {
        node.className = 'PlaygroundEditorTheme__boxContent';
      }
      if (node.children && Array.isArray(node.children)) {
        node.children = node.children.map(child => processNode(child));
      }
      return node;
    };

    lexicalData.root = processNode(lexicalData.root);
    const editorState = editor.parseEditorState(lexicalData);

    let content = '';
    editor.setEditorState(editorState);
    editor.update(() => {
      content = $generateHtmlFromNodes(editor);
    });

    return content;
  };

  // 미리보기용 문제 상세 정보 가져오기 함수
  const fetchProblemDetailsForPreview = async (problemIds) => {
    try {
      setIsLoading(true);
      const token = await getAccessToken();
      const details = {};
      
      // 배치로 모든 문제의 상세 정보를 한 번에 가져옵니다
      try {
        const problemIdsString = problemIds.join(',');
        const batchResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/problems/print/page?problem_ids=${problemIdsString}&page=1&per_page=50`,
          {
            headers: {
              'Authorization': token,
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          }
        );

        if (batchResponse.ok) {
          const batchData = await batchResponse.json();
          
          if (batchData.status === 'success' && batchData.data) {
            // 각 문제에 대한 결과 처리
            batchData.data.forEach(problemData => {
              const problemId = problemData.problem_id;
              if (problemData?.lexical_data?.lexical_data) {
                const content = convertLexicalToHtml(problemData.lexical_data.lexical_data);
                details[problemId] = { content };
              } else {
                details[problemId] = { content: '문제 내용을 불러올 수 없습니다.' };
              }
            });

            // 응답에 없는 문제들 처리
            problemIds.forEach(problemId => {
              if (!details[problemId]) {
                details[problemId] = { content: '문제 내용을 불러올 수 없습니다.' };
              }
            });
          }
        } else {
          // 배치 API 실패시 모든 문제를 오류 처리
          problemIds.forEach(problemId => {
            details[problemId] = { content: '문제 내용을 불러올 수 없습니다.' };
          });
        }
      } catch (error) {
        console.error('배치 문제 로드 실패:', error);
        // 오류 발생시 모든 문제를 오류 처리
        problemIds.forEach(problemId => {
          details[problemId] = { content: '문제 내용을 불러올 수 없습니다.' };
        });
      }
      
      setPreviewProblemDetails(details);
    } catch (error) {
      console.error('문제 상세 정보 로드 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 프리셋 미리보기 가져오기 함수
  const previewPreset = async () => {
    if (!selectedPreset) return;
    
    try {
      setIsLoading(true);
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/${selectedPreset.preset_id}`, {
        headers: { 'Authorization': token }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        const presetData = data.data;
        setPreviewPresetData(presetData);
        
        // 문제 상세 정보 가져오기
        if (presetData.preset_info && Array.isArray(presetData.preset_info) && presetData.preset_info.length > 0) {
          const problemIds = presetData.preset_info.map(p => p.problem_id);
          await fetchProblemDetailsForPreview(problemIds);
        }
        
        setIsPreviewModalOpen(true);
      }
    } catch (error) {
      console.error('프리셋 미리보기 로드 실패:', error);
      alert('미리보기를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[999]">
      <div className="pointer-events-auto fixed flex w-[95vw] flex-col lg:w-[600px] rounded-3xl border border-gray-200 bg-white shadow-xl"
        style={buttonRect ? {
          bottom: `${window.innerHeight - buttonRect.top + 16}px`,
          // window.innerWidth >= 1024 (lg breakpoint)를 사용하여 데스크톱 구분
          left: window.innerWidth >= 1024 ? `${buttonRect.left + 2}px` : '50%',
          transform: window.innerWidth >= 1024 ? 'none' : 'translateX(-50%)',
          maxHeight: `calc(${buttonRect.top - 16}px - 1rem)`,
          height: 'auto'
        } : {
          bottom: 'calc(68px + 2rem + 56px + 1rem + env(safe-area-inset-bottom, 0px))',
          left: '50%',
          transform: 'translateX(-50%)',
          maxHeight: '70vh',
          height: 'auto'
        }}
      >
        {/* 화살표: 데스크톱은 Plus 버튼 중심, 모바일은 팝업 중앙 */}
        <div 
          className="absolute -bottom-2"
          style={{
            left: window.innerWidth >= 1024 && buttonRect 
              ? `${(buttonRect.width / 2) - 2}px` // Plus 버튼 중심 위치
              : '50%', // 모바일: 팝업 중앙
            transform: window.innerWidth >= 1024 && buttonRect 
              ? 'translateX(-50%)' // 화살표 중앙 정렬
              : 'translateX(-50%)'
          }}
        >
          <div className="size-5 rotate-45 bg-white" />
        </div>
        
        <div className="shrink-0 p-4 pb-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">테스트 생성</h2>
            <div className="flex gap-2">
              <button 
                onClick={() => setIsMinimized(prev => !prev)} 
                className="text-gray-500 hover:text-gray-700"
              >
                <span className="text-xl">−</span>
              </button>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {!isMinimized ? (
          <form onSubmit={handleSubmit} className="flex flex-col space-y-3 p-4" style={{ overflow: 'visible' }}>
            <div>
              {/* <label className="mb-1 block text-sm font-medium text-gray-700">테스트 타입 선택</label> */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { id: 'alpha', symbol: 'α', label: 'Alpha' },
                  // { id: 'beta', symbol: 'β', label: 'Beta' },
                  // { id: 'gamma', symbol: 'γ', label: 'Gamma' },
                  { id: 'custom', symbol: '𝐂', label: 'Custom' },
                  { id: 'delta', symbol: 'δ', label: 'Delta' },
                  { id: 'epsilon', symbol: 'ε', label: 'Epsilon' }
                ].map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => {
                      setExamType(type.id);
                      if (type.id === 'delta') {
                        setSelectedAddressesForExam([]);
                      } else if (type.id === 'alpha' || type.id === 'custom' || type.id === 'epsilon') {
                        setSelectedAddressesForExam([]);
                        setDeltaSelections([]);
                      } else {
                        setDeltaSelections([]);
                      }
                      setSelectedPreset(null);
                      setSelectedReview(null);
                    }}
                    className={`rounded-3xl px-3 py-1.5 text-sm font-medium transition-colors ${
                      examType === type.id
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span className={examType === type.id ? '' : 'text-green-600'}>{type.symbol}</span> {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 모드별 옵션 영역 (고정 높이) */}
            <div className="h-[140px] space-y-3 overflow-y-auto" style={{ overflowY: (examType === 'custom' && isPresetDropdownOpen) || (examType === 'epsilon' && isReviewDropdownOpen) ? 'visible' : 'auto' }}>
            {/* 중복 출제 방지 모드 체크박스 */}
            <div className={`flex items-center gap-2 rounded-lg bg-blue-50 p-3 transition-opacity ${examType === 'epsilon' ? 'opacity-30 pointer-events-none' : ''}`}>
              <input
                type="checkbox"
                id="solvedExceptionMode"
                checked={solvedExceptionMode}
                onChange={(e) => setSolvedExceptionMode(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              {/* 레거시 출제는 스탬프 기준이라 신규 시험지 기록을 보지 않는다. 근거는 handleSubmit의
                  solved_exception 주석(BE 회신 2026-08-29 §1 2-4). 다른 출제 화면과 문구를
                  통일하기 전에 그것부터 읽을 것. */}
              <label htmlFor="solvedExceptionMode" className="flex-1 cursor-pointer text-sm">
                <span className="font-medium text-gray-700">문제 중복 출제 방지 모드</span>
                <span className="ml-2 text-xs text-gray-500">
                  {examType === 'custom' 
                    ? '(프리셋 모드에서는 중복 문제가 있을 경우 경고만 표시됩니다)'
                    : '(이미 풀었던 문제를 제외하고 새로운 문제만 출제됩니다)'}
                </span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {examType === 'alpha' && (
                <>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">난이도 <span className="text-xs font-normal text-gray-500">(1.0 ~ 5.0)</span></label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={difficulty}
                      onChange={(e) => setDifficulty(e.target.value)}
                      className="alpha-number-input w-full rounded-2xl border px-2 text-center text-base"
                      style={{ height: '53px' }}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">문제 수</label>
                    <input
                      type="number"
                      min="1"
                      value={totalProblems}
                      onChange={(e) => setTotalProblems(parseInt(e.target.value))}
                      className="alpha-number-input w-full rounded-2xl border px-2 text-center text-base"
                      style={{ height: '53px' }}
                    />
                  </div>
                </>
              )}

              {(examType === 'beta' || examType === 'gamma') && (
                <>
                  <div className="col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      선택된 유형 ({selectedAddressesForExam.length}개 선택됨)
                    </label>
                    <div className="max-h-[150px] overflow-y-auto rounded-2xl border p-2">
                      {selectedAddressesForExam.length > 0 ? (
                        <div className="flex flex-wrap gap-1 text-sm text-gray-600">
                          {[...selectedAddressesForExam]
                            .sort((a, b) => a.localeCompare(b))
                            .map((address, idx) => (
                              <span 
                                key={idx} 
                                className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1"
                              >
                                <span className="max-w-[200px] truncate">{address}</span>
                              </span>
                            ))}
                        </div>
                      ) : (
                        <div className="py-2 text-center text-sm text-gray-500">
                          결과 맵에서 유형을 선택해주세요
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700">문제 수</label>
                    <input
                      type="number"
                      min="1"
                      value={totalProblems}
                      onChange={(e) => setTotalProblems(parseInt(e.target.value))}
                      className="w-full rounded-2xl border px-2 text-sm"
                      style={{ height: '53px' }}
                    />
                  </div>
                </>
              )}

              {examType === 'delta' && (
                <div className="col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    선택된 중유형과 레벨 ({deltaSelections.length}개 선택됨)
                  </label>
                  <div className="max-h-[150px] overflow-y-auto rounded-2xl border p-2">
                    {deltaSelections.length > 0 ? (
                      <div className="flex flex-wrap gap-1 text-sm text-gray-600">
                        {deltaSelections.map((selection, idx) => {
                          const [address, level] = selection.split(',');
                          return (
                            <span 
                              key={idx} 
                              className="inline-block rounded-3xl bg-gray-100 px-2 py-1"
                            >
                              {address} (Lv.{level})
                              <button 
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeltaSelections(prev => 
                                    prev.filter(item => item !== selection)
                                  );
                                }}
                                className="ml-1 text-gray-500 hover:text-gray-700"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="py-2 text-center text-sm text-gray-500">
                        결과 맵에서 중유형과 레벨을 선택해주세요
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {examType === 'epsilon' && (
                <div className="col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    오답노트 선택
                    <span className="ml-2 text-xs font-normal text-gray-500">(오답노트 목록에서 선택하여 출제합니다)</span>
                  </label>
                  <div className="relative" ref={reviewDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsReviewDropdownOpen(prev => !prev)}
                      className="flex h-[53px] w-full items-center justify-between rounded-2xl border px-3 py-2 text-sm"
                    >
                      {selectedReview ? (
                        <span className="flex items-center gap-2 truncate">
                          <span className="inline-grid size-5 shrink-0 place-items-center rounded-full border border-primary text-[11px] font-bold text-primary">{selectedReview.seq}</span>
                          <span className="truncate">TID({selectedReview.exam_rounds?.join(', ') || '-'})</span>
                        </span>
                      ) : (
                        <span className="text-gray-500">오답노트를 선택해주세요</span>
                      )}
                      <ChevronDown size={16} className={`shrink-0 transition-transform ${isReviewDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isReviewDropdownOpen && (
                      <div
                        className="absolute bottom-full z-10 mb-1 max-h-60 w-full overflow-y-auto rounded-3xl border bg-white shadow-lg"
                      >
                        {isLoadingReviews ? (
                          <div className="py-2 text-center text-gray-500">로딩 중...</div>
                        ) : reviewList.length > 0 ? (
                          reviewList.map(review => (
                            <div
                              key={review.review_id}
                              onClick={() => {
                                setSelectedReview(review);
                                setIsReviewDropdownOpen(false);
                              }}
                              className="cursor-pointer border-b px-3 py-2 last:border-0 hover:bg-gray-100"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 truncate font-medium">
                                    <span className="inline-grid size-5 shrink-0 place-items-center rounded-full border border-primary text-[11px] font-bold text-primary">{review.seq}</span>
                                    <span className="truncate">TID({review.exam_rounds?.join(', ') || '-'})</span>
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {review.author} · 오답 {review.wrong_problems}/{review.total_problems}문항
                                  </div>
                                </div>
                                <div className="shrink-0 text-xs text-gray-400">
                                  {new Date(review.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="py-2 text-center text-gray-500">
                            사용 가능한 오답노트가 없습니다.
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {selectedReview && (
                    <div className="mt-3 rounded border bg-gray-50 p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <h3 className="flex items-center gap-2 font-medium">
                          <span className="inline-grid size-5 shrink-0 place-items-center rounded-full border border-primary text-[11px] font-bold text-primary">{selectedReview.seq}</span>
                          <span className="truncate">TID({selectedReview.exam_rounds?.join(', ') || '-'})</span>
                        </h3>
                        <span className="text-sm text-gray-600">
                          오답 {selectedReview.wrong_problems}/{selectedReview.total_problems}문항
                        </span>
                      </div>
                      <div className="mb-2 text-xs text-gray-600">
                        {selectedReview.memo || "메모 없음"}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          작성자: <span className="font-medium">{selectedReview.author}</span>
                        </div>
                        <div className="text-sm text-gray-700">
                          회차: {selectedReview.exam_rounds?.join(', ') || '-'}
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        생성일: {new Date(selectedReview.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {examType === 'custom' && (
                <div className="col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    커스텀 선택
                    <span className="ml-2 text-xs font-normal text-gray-500">(북마크 목록의 커스텀만 선택할 수 있습니다)</span>
                  </label>
                  <div className="relative" ref={presetDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsPresetDropdownOpen(prev => !prev)}
                      className="flex h-[53px] w-full items-center justify-between rounded-2xl border px-3 py-2 text-sm"
                    >
                      {selectedPreset ? (
                        <span>{selectedPreset.preset_name}</span>
                      ) : (
                        <span className="text-gray-500">커스텀을 선택해주세요</span>
                      )}
                      <ChevronDown size={16} className={`transition-transform ${isPresetDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {isPresetDropdownOpen && (
                      <div 
                        className="absolute bottom-full z-10 mb-1 max-h-60 w-full overflow-y-auto rounded-3xl border bg-white shadow-lg"
                      >
                        {isLoading ? (
                          <div className="py-2 text-center text-gray-500">로딩 중...</div>
                        ) : presets.length > 0 ? (
                          presets.map(preset => (
                            <div
                              key={preset.preset_id}
                              onClick={() => {
                                setSelectedPreset(preset);
                                setIsPresetDropdownOpen(false);
                              }}
                              className="cursor-pointer border-b px-3 py-2 last:border-0 hover:bg-gray-100"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-medium">{preset.preset_name}</div>
                                  <div className="text-xs text-gray-500">
                                    {preset.author_name} · {preset.tree_name} · {preset.length}문항
                                  </div>
                                </div>
                                <div className="text-sm">
                                  난이도: {getDifficultyLabel(preset.difficulty)}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="py-2 text-center text-gray-500">
                            사용 가능한 커스텀이 없습니다.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {selectedPreset && (
                    <div className="mt-3 rounded border bg-gray-50 p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <h3 className="font-medium">{selectedPreset.preset_name}</h3>
                        <span className="text-sm text-gray-600">난이도: {getDifficultyLabel(selectedPreset.difficulty)}</span>
                      </div>
                      <div className="mb-2 text-xs text-gray-600">
                        {selectedPreset.description || "설명 없음"}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          문항 수: <span className="font-medium">{selectedPreset.length}</span>
                        </div>
                        {renderSummary(selectedPreset.summary)}
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        마지막 업데이트: {new Date(selectedPreset.updated_at).toLocaleDateString()}
                      </div>
                      
                      {/* 미리보기 버튼 추가 */}
                      <div className="mt-3 flex justify-end">
                        <button
                          type="button"
                          onClick={previewPreset}
                          className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Eye size={16} className="mr-1" />
                          미리보기
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            </div>

            <div className="mt-4 flex shrink-0 justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-3xl bg-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  isSubmitCooldown ||
                  !examType ||
                  (examType === 'custom' && !selectedPreset) ||
                  (examType === 'epsilon' && !selectedReview)
                }
                className="rounded-3xl bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-50"
              >
                {isSubmitting ? '생성 중...' : '생성'}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-4 text-sm text-gray-600">
            {examType ? `${examType.toUpperCase()} 시험 생성 중...` : '시험 유형을 선택해주세요'}
          </div>
        )}
      </div>
      
      {/* 미리보기 모달 */}
      {isPreviewModalOpen && previewPresetData && (
        <div 
          className="pointer-events-auto fixed inset-0 flex items-center justify-center"
          style={{ 
            zIndex: 9999980,
            paddingTop: window.innerWidth < 768 ? 'env(safe-area-inset-top, 0px)' : '0px',
            paddingBottom: window.innerWidth < 768 ? 'calc(68px + env(safe-area-inset-bottom, 0px))' : '0px',
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          }}
          onClick={() => setIsPreviewModalOpen(false)}
        >
          <div 
            className="flex h-5/6 w-[850px] flex-col overflow-hidden rounded-3xl bg-white shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b p-4">
              <h2 className="text-xl font-bold">
                {previewPresetData.preset_name} - 미리보기
              </h2>
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                ✕
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              <PreviewSection
                selectedProblems={previewPresetData.preset_info || []}
                problemDetails={previewProblemDetails}
                mapData={{ field_info: { tree_name: previewPresetData.tree_name } }}
                stamp={{ edu_ver: previewPresetData.edu_ver }}
                onProblemClick={() => {}}
                setPrintFunction={() => {}}
              />
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

export default CreateExamModal;
